import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';

export interface VisibleColumns {
  actuals_to_date: {
    costs: boolean;
    perc: boolean;
    units: boolean;
  };
  current_period: {
    months: boolean;
    quarters: boolean;
  };
  forecast: {
    months: boolean;
    quarters: boolean;
    years: boolean;
  };
  historicals: {
    months: boolean;
    quarters: boolean;
    years: boolean;
  };
  overall_budget: {
    original: boolean;
    primary: boolean;
    unit_cost: boolean;
    units: boolean;
    var_cost: boolean;
    var_perc: boolean;
  };
  remaining_budget: {
    costs: boolean;
    perc: boolean;
    units: boolean;
  };
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CheckboxComponent, FormsModule, AsyncPipe],
  selector: 'aux-column-chooser-legacy',
  styles: [],
  templateUrl: './column-chooser.component.html',
})
export class ColumnChooserLegacyComponent {
  private cdr = inject(ChangeDetectorRef);
  private launchDarklyService = inject(LaunchDarklyService);

  columns!: VisibleColumns;
  defaultColumns: VisibleColumns = {
    actuals_to_date: {
      costs: true,
      perc: true,
      units: true,
    },
    current_period: {
      months: true,
      quarters: true,
    },
    forecast: {
      months: true,
      quarters: true,
      years: true,
    },
    historicals: {
      months: true,
      quarters: true,
      years: true,
    },
    overall_budget: {
      original: true,
      primary: true,
      unit_cost: false,
      units: true,
      var_cost: true,
      var_perc: true,
    },
    remaining_budget: {
      costs: true,
      perc: true,
      units: true,
    },
  };
  ref = inject<
    CustomOverlayRef<
      unknown,
      {
        columns: VisibleColumns;
      }
    >
  >(CustomOverlayRef);
  showActualsToDate$ = this.launchDarklyService.select$(
    (flags) => flags.budget_actuals_to_date_units
  );

  constructor() {
    this.columns = this.ref.data?.columns || this.defaultColumns;
  }

  isGroupChecked(group: keyof VisibleColumns): boolean {
    return Object.values({ ...this.defaultColumns[group], ...this.columns?.[group] }).every(
      (el) => el
    );
  }

  isGroupIndeterminate(group: keyof VisibleColumns): boolean {
    return (
      Object.values({ ...this.defaultColumns[group], ...this.columns?.[group] }).some((el) => el) &&
      !this.isGroupChecked(group)
    );
  }

  onConfirm() {
    this.ref.close(this.columns);
  }

  onGroupChange(group: keyof VisibleColumns) {
    const columnGroup = { ...this.defaultColumns[group], ...this.columns?.[group] };
    const newValue = !this.isGroupChecked(group);
    const newGroup = Object.keys(columnGroup).reduce(
      (acc, key: string) => ({
        ...acc,
        [key]: newValue,
      }),
      {}
    );

    Object.assign(this.columns, { [group]: newGroup });
    this.cdr.detectChanges();
  }
}
